import React, { useState, useMemo } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import './zo-date-picker.scss';

interface DatePickerProps {
  /** Provide the label of date picker. */
  title?: string;
  /** Label for the input field */
  label: string;
  /** Whether the field is required */
  isRequired?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Callback on change */
  onChange: (val: any) => void;
  /** Current value */
  value: string | dayjs.Dayjs | null;
  /** Disable input */
  disabled?: boolean;
  /** Disable past dates */
  disablePast?: boolean;
  /** Disable future dates */
  disableFuture?: boolean;
  /** Error state */
  error?: boolean;
  /** Minimal selectable date */
  minDate?: any;
  /** Maximum selectable date */
  maxDate?: any;
  /** Adornment at start */
  startAdornment?: React.JSX.Element;
  /** Adornment at end */
  endAdornment?: React.JSX.Element;
  /** Style props for container */
  dateFeildStyleProps?: React.CSSProperties;
  /** Extra input styles */
  inputFieldProps?: React.CSSProperties;
  /** Height of input */
  height?: number;
  width?: number;
  /** Array of allowed dates (parseable by dayjs) - only these dates are enable for selection */
  allowedDates?: string[];
  inputSx?: any;
  /** Hide label when value is selected or picker is open */
  hideLabelWhenValueOrOpen?: boolean;
  /** Hide the floating label */
  hideFloatingLabel?: boolean;
}

export const ZoDatePicker: React.FC<DatePickerProps> = ({
  label,
  title,
  isRequired = false,
  onChange,
  placeholder,
  value,
  disabled = false,
  disablePast = false,
  disableFuture = false,
  error = false,
  minDate,
  maxDate,
  startAdornment,
  endAdornment,
  dateFeildStyleProps,
  inputFieldProps,
  height = 42,
  width,
  allowedDates = [],
  inputSx,
  hideLabelWhenValueOrOpen = false,
  hideFloatingLabel = false,
}) => {
  const [open, setOpen] = useState(false);

  // build a Set of allowed timestamp values (start-of-day) for quick lookup
  const allowedSet = useMemo(() => {
    return new Set<number>(
      allowedDates.map((d) => dayjs(d).startOf('day').valueOf())
    );
  }, [allowedDates]);

  // determine if a date should be disabled
  const shouldDisable = (date: Dayjs) => {
    if (!allowedDates.length) return false;
    const val = date.startOf('day').valueOf();
    return !allowedSet.has(val);
  };

  // Check if value is empty (null, undefined, or empty string)
  const hasValue = value !== null && value !== undefined && value !== '';

  const inputFeildStyleContainer = {
    fontSize: '14px',
    color: `${hasValue ? '#091E42' : '#8f8f8f'}`,
    caretColor: 'transparent',
    cursor: 'pointer',
    zIndex: 99,
    ...inputFieldProps,
  };
  const handleClearDate = () => {
    onChange(null); // Reset the date value
    setOpen(false); // Close the popup
  };

  const CustomActionBar = ({ onAccept, onCancel }: any) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px 16px',
        borderTop: '1px solid #e0e0e0',
      }}
    >
      <div
        className="primary-font-semibold"
        onClick={handleClearDate}
        style={{
          color: '#4481B4',
          fontSize: 14,
          textAlign: 'center',
          textDecorationLine: 'underline',
          cursor: 'pointer',
          lineHeight: '30px',
        }}
      >
        Clear
      </div>

      <div>
        <button
          onClick={onCancel}
          className="primary-font-semibold"
          style={{
            marginRight: 12,
            width: 62,
            height: 32,
            borderRadius: 6,
            fontSize: 14,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#82807f',
          }}
        >
          Cancel
        </button>
        <button
          className="primary-font-semibold"
          onClick={onAccept}
          style={{
            background: '#32A7E8',
            border: 'none',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '14px',
            width: 62,
            height: 32,
            borderRadius: 3,
          }}
        >
          Done
        </button>
      </div>
    </div>
  );

  // parse incoming value to Dayjs
  const parseDate = (dateValue: string | dayjs.Dayjs | null) => {
    if (!dateValue) return null;
    if (dayjs.isDayjs(dateValue)) return dateValue;
    const formats = ['DD/MM/YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD'];
    for (const fmt of formats) {
      const parsed = dayjs(dateValue, fmt);
      if (parsed.isValid()) return parsed;
    }
    const fallback = dayjs(dateValue);
    return fallback.isValid() ? fallback : null;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="zoDatePicker" style={{ width: '100%' }}>
        {/* {title && (
          <span
            className={`datepicker_title ${error
              ? 'datepicker_title__error'
              : isFocused
                ? 'datepicker_title__focused'
                : ''
              }`}
            style={{
              color: isFocused ? '#32a7e8' : '#000000',
            }}
          >
            {title}
          </span>
        )} */}
        {title && (
          <span
            className={`datepicker_title ${
              error
                ? 'datepicker_title__error'
                : open
                  ? 'datepicker_title__active'
                  : ''
            }`}
            style={{ color: open ? '#32a7e8' : '#000000' }}
          >
            {title}
            {isRequired && <span className="input_text__asterisk">*</span>}
          </span>
        )}

        <DatePicker
          label={hideLabelWhenValueOrOpen && (hasValue || open) ? '' : label}
          format="DD-MM-YYYY"
          disabled={disabled}
          disablePast={disablePast}
          disableFuture={disableFuture}
          minDate={minDate}
          maxDate={maxDate}
          open={!disabled && open}
          onClose={() => setOpen(false)}
          shouldDisableDate={shouldDisable}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: `${height}px`,
              ...(width ? { width: `${width}px` } : {}),
              '& fieldset': {
                borderColor: open
                  ? '#32a7e8'
                  : disabled
                    ? '#f0f0f0'
                    : '#dcdcdc',
                borderWidth: '1px',
                borderRadius: '6px',
                backgroundColor: '#fff',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#32a7e8',
                borderWidth: '1px',
                borderRadius: '6px',
                backgroundColor: '#fff',
              },
              '&.Mui-disabled': disabled
                ? { backgroundColor: '#f0f0f0f0' }
                : {},
            },
          }}
          onChange={(val) => onChange(val)}
          value={parseDate(value)}
          slotProps={{
            popper: {
              sx: {
                zIndex: 9999999,
              },
            },
            textField: {
              onClick: () => setOpen(true),
              placeholder,
              disabled,
              required: isRequired,
              InputLabelProps: {
                className: 'primary-font-regular',
                style: {
                  color: `${hasValue || open ? '#091E42' : '#BFC0C2'}`,
                  ...(hideLabelWhenValueOrOpen
                    ? {
                        display: !hasValue && !open ? 'block' : 'none',
                        marginTop: -10,
                      }
                    : {
                        marginTop: hasValue || open ? 0 : -10,
                      }),
                },
                shrink: !hideFloatingLabel,
              },
              inputProps: {
                className: 'primary-font-regular',
                sx: {
                  '&::placeholder': {
                    color: open ? '#091E42' : '#BFC0C2',
                    opacity: 1,
                    fontSize: '14px',
                    fontFamily: 'Segoe-UI-VF',
                    display: 'block !important',
                  },
                },
                style: inputFeildStyleContainer,
              },
              InputProps: {
                startAdornment: startAdornment && (
                  <div style={{ zIndex: 99, display: 'flex' }}>
                    {startAdornment}
                  </div>
                ),
                endAdornment: endAdornment && (
                  <div style={{ zIndex: 99, display: 'flex' }}>
                    {endAdornment}
                  </div>
                ),
                placeholder: placeholder,
                error,
                sx: {
                  ...dateFeildStyleProps,
                  ...inputSx,
                },
              },
            },
          }}
        />
      </div>
    </LocalizationProvider>
  );
};
